'use client';

import { useState } from 'react';
import type { CSSProperties, ChangeEvent, FormEvent } from 'react';
import { Check } from 'lucide-react';
import { AboutUsHeader } from '@/features/about-us/components/AboutUsHeader';
import { cn } from '@/shadcn/lib/utils';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { validateMobilePhoneE164 } from '@/shared/validation/validators/validateMobilePhoneE164';

const IMAGE_BASE_PATH = '/images/about-us/contact';
const BRAND_PATH = '/images/brand';

const SUBJECTS = [
    'Reklama na Realico',
    'Partnerstvo',
    'Investičné možnosti',
    'Technická podpora',
    'Iné',
];

const TABS = ['Email', 'Telefón'] as const;

type ContactTab = (typeof TABS)[number];

const inputBase = cn(
    'w-full',
    'h-[46px]',
    'rounded-[8px]',
    'border border-[#E5E5E5]',
    'bg-[#FAFAFA]',
    'px-[14px]',
    'text-[13px]',
    'font-inherit',
    'text-[#111]',
    'outline-none',
    // focus
    'focus:border-[#F3C300]',
    'focus:bg-white',
    'focus:ring-[3px]',
    'focus:ring-[rgba(243,195,0,0.15)]',
);
const cardLeftShadow = cn(
    'md:pointer-events-none',
    'md:absolute',
    'md:-left-[90px]',
    'md:top-1/2 -translate-y-1/2',

    'md:h-[100%]',
    'md:w-[90px]',

    'md:rounded-full',
    'md:bg-black/[0.06]',
    'md:blur-[35px]',
);
/*
const validationSchema = Yup.object({
    name: Yup.string().required('Meno je povinné'),

    company: Yup.string(),

    phone: Yup.string().when('$tab', {
        is: 'Telefón',
        then: (schema) => schema.required('Telefón je povinný'),
    }),

    email: Yup.string().when('$tab', {
        is: 'Email',
        then: (schema) => schema.email('Neplatný email').required('Email je povinný'),
    }),

    subject: Yup.string(),

    message: Yup.string().required('Správa je povinná'),

    agreed: Yup.boolean().oneOf([true], 'Musíte súhlasiť'),
});
*/
const getValidationSchema = (tab: ContactTab) =>
    Yup.object({
        name: Yup.string().min(5, 'Minimálne 5 znakov').required('Meno je povinné'),
        company: Yup.string(),

        phone:
            tab === 'Telefón'
                ? Yup.string()
                      .required('Telefón je povinný')
                      .test('phone-e164', function (value) {
                          const result = validateMobilePhoneE164(value, {
                              allowedCountries: ['SK', 'CZ'],
                          });

                          if (result.ok) return true;

                          return this.createError({
                              message: result.message,
                          });
                      })
                : Yup.string(),
        email:
            tab === 'Email'
                ? Yup.string().email('Neplatný email').required('Email je povinný')
                : Yup.string().email('Neplatný email'),

        subject: Yup.string(),
        message: Yup.string().min(10, 'Minimálne 10 znakov').required('Správa je povinná'),
        agreed: Yup.boolean().oneOf([true], 'Musíte súhlasiť'),
    });

export default function Contact() {
    const [tab, setTab] = useState<ContactTab>('Email');
    const [sent, setSent] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);

    const formik = useFormik({
        initialValues: {
            name: '',
            company: '',
            phone: '',
            email: '',
            subject: '',
            message: '',
            agreed: false,
        },
        validationSchema: getValidationSchema(tab),
        onSubmit: async (values) => {
            setSubmitError(null);

            try {
                // throw new Error();
                const payload = {
                    preferredContact: tab,
                    ...values,
                };

                // TODO: API request
                // const res = await fetch('/api/contact', {...});
                // if (!res.ok) throw new Error();

                console.log(payload);
                setSent(true);
            } catch {
                setSubmitError('Správu sa nepodarilo odoslať. Skúste to prosím neskôr znova.');
            } finally {
                //
            }
        },
    });

    function reset() {
        formik.resetForm();
        setTab('Email');
        setSent(false);
    }

    const hasError = (field: keyof typeof formik.values) =>
        Boolean(formik.touched[field] && formik.errors[field]);

    const errorClass = (field: keyof typeof formik.values) =>
        hasError(field) &&
        'border-[#d32f2f] bg-[#fff5f5] focus:border-[#d32f2f] focus:ring-[rgba(211,47,47,0.15)]';

    function renderError(field: keyof typeof formik.values) {
        if (!hasError(field)) return null;

        return <p className="mt-[4px] text-[11px] text-[#d32f2f]">{formik.errors[field]}</p>;
    }

    return (
        <div>
            <section
                className="relative w-full min-h-[400px] flex flex-col"
                style={{
                    background: 'linear-gradient(to bottom, #e0aa03, #f3c300)',
                }}
            >
                <div className="cust-container relative flex-1 flex flex-col">
                    {/* Header */}
                    <div className="py-[15px]">
                        <AboutUsHeader />
                    </div>

                    <div className="flex-1 flex flex-col">
                        <img
                            src={`${IMAGE_BASE_PATH}/kontakt_bgr_icons.png`}
                            alt=""
                            aria-hidden
                            className="pointer-events-none absolute inset-0 h-full w-full select-none min-w-[1000px]"
                            style={{
                                objectFit: 'contain',
                                objectPosition: 'center',
                                zIndex: 1,
                                opacity: 1,
                                transform: 'scale(1.2)',
                                transformOrigin: 'center',
                            }}
                        />

                        <h1
                            className={cn(
                                'relative z-[100]',
                                'mt-[50px]',
                                'text-black',
                                'text-[30px]',
                                'font-bold',
                                'leading-[1.14]',
                                'tracking-[-0.022em]',
                                'md:mt-[80px]',
                                'md:text-[35px] md:mt-[90px]',
                                'xl:text-[40px] xl:mt-[100px]',
                            )}
                        >
                            <span style={{ color: '#fff' }}>Začína to dialógom.</span>
                            <br />
                            Ozvite sa nám.
                        </h1>
                        <img
                            src={`${IMAGE_BASE_PATH}/kontakt_photo.png`}
                            alt=""
                            aria-hidden
                            className={cn(
                                'relative pointer-events-none select-none z-[2] translate-y-[11.6%] w-[70%] max-w-[400px] mx-auto mt-auto xl:-translate-x-[50%]',
                                'md:ml-auto md:mr-0 md:-mt-[190px]',
                                'xl:max-w-[450px] xl:-mt-[230px]',
                                '2xl:max-w-[500px] xl:-mt-[240px]',
                            )}
                        />
                    </div>

                    <img
                        src={`${IMAGE_BASE_PATH}/kontakt_plane.svg`}
                        alt=""
                        aria-hidden
                        className="absolute pointer-events-none w-[70px] bottom-0 left-[20px] translate-y-[50%] z-[30] xl:w-[100px]"
                    />
                </div>
            </section>

            <section className="bg-white text-center my-[80px] xl:my-[100px]">
                <div className="cust-container">
                    <p className="mx-auto text-[14px] text-[#555555] leading-[1.6] md:text-[15px] xl:text-[16px]">
                        Potrebujete pomôcť s automatizáciou inzerátov, zvýšením ich dosahu alebo
                        máte technickú otázku?
                        <br />
                        Napíšte nám svoje požiadavky a spoločne nájdeme najlepšie riešenie pre váš
                        rast na Realico.
                    </p>
                </div>
            </section>

            <section className="mx-auto">
                <div className="cust-container">
                    <div className={cn('relative flex flex-col', 'lg:flex-row lg:justify-end')}>
                        <div
                            className={cn(
                                'p-[20px] bg-white',
                                'rounded-[15px_15px_0_0]',
                                'shadow-[0_4px_40px_rgba(0,0,0,0.12),0_1px_4px_rgba(0,0,0,0.06)]',
                                'flex flex-col',
                                'p-[25px]',
                                'lg:relative lg:z-2 lg:p-[20px] lg:m-[10px] lg:rounded-[15px]',
                                'xl:rounded-[20px]',
                            )}
                        >
                            {sent ? (
                                <div
                                    className={cn(
                                        'py-[20px] flex h-full flex-col items-center justify-center',
                                        'gap-4',
                                    )}
                                >
                                    <div
                                        className={cn(
                                            'flex items-center justify-center',
                                            'w-[56px] h-[56px]',
                                            'rounded-full',
                                            'bg-[#F3C300]',
                                        )}
                                    >
                                        <Check size={24} color="#fff" strokeWidth={3} />
                                    </div>

                                    <h3
                                        className={cn(
                                            'm-0',
                                            'text-[22px]',
                                            'font-bold',
                                            'text-black',
                                            'md:text-[23px]',
                                            'xl:text-[25px]',
                                        )}
                                    >
                                        Ďakujeme!
                                    </h3>

                                    <p
                                        className={cn(
                                            'm-0',
                                            'text-center',
                                            'text-[15px]',
                                            'text-[#666]',
                                            'xl:text-[16px]',
                                        )}
                                    >
                                        Vaša správa bola odoslaná. Ozveme sa vám čo najskôr.
                                    </p>

                                    <button
                                        type="button"
                                        onClick={reset}
                                        className={cn(
                                            'bg-transparent',
                                            'border-0',
                                            'cursor-pointer',
                                            'underline',
                                            'text-[13px]',
                                            'text-[#888]',
                                            'transition-opacity',
                                            'hover:opacity-70',
                                            'md:text-[14px]',
                                            'xl:text-[15px]',
                                        )}
                                    >
                                        Odoslať ďalšiu správu
                                    </button>
                                </div>
                            ) : (
                                <form
                                    onSubmit={formik.handleSubmit}
                                    noValidate
                                    className="flex h-full flex-col"
                                >
                                    <p className="mb-[14px] text-center text-[13px] font-medium text-[#888]">
                                        Preferovaný kontakt:
                                    </p>

                                    <div
                                        className={cn(
                                            'mb-5',
                                            'flex w-full items-center',
                                            'h-[50px]',
                                            'gap-[2px]',
                                            'rounded-[7.5px]',
                                            'bg-[#F5F5F5]',
                                            'p-1',
                                        )}
                                    >
                                        {TABS.map((t) => {
                                            const active = tab === t;

                                            return (
                                                <button
                                                    key={t}
                                                    type="button"
                                                    onClick={() => setTab(t)}
                                                    className={cn(
                                                        'h-[42px] flex-1 rounded-[6px]',
                                                        'text-[12.5px] font-medium',
                                                        'transition-all duration-200',
                                                        active
                                                            ? 'bg-white text-[#111] shadow-[0_2px_8px_rgba(0,0,0,0.10)]'
                                                            : 'bg-transparent text-[#777]',
                                                    )}
                                                >
                                                    {t}
                                                </button>
                                            );
                                        })}
                                    </div>

                                    <div className="flex flex-col gap-[8px]">
                                        <div className="flex gap-[8px]">
                                            <div className="flex-1">
                                                <input
                                                    className={cn(inputBase, errorClass('name'))}
                                                    type="text"
                                                    placeholder="Vaše meno"
                                                    name="name"
                                                    value={formik.values.name}
                                                    onChange={formik.handleChange}
                                                    onBlur={formik.handleBlur}
                                                />
                                                {renderError('name')}
                                            </div>
                                            <div className="flex-1">
                                                <input
                                                    className={cn(inputBase, errorClass('company'))}
                                                    type="text"
                                                    placeholder="Firma (nepovinné)"
                                                    name="company"
                                                    value={formik.values.company}
                                                    onChange={formik.handleChange}
                                                    onBlur={formik.handleBlur}
                                                />
                                                {renderError('company')}
                                            </div>
                                        </div>

                                        <div className="flex gap-[8px]">
                                            <div className="flex-1">
                                                <input
                                                    className={cn(inputBase, errorClass('phone'))}
                                                    type="tel"
                                                    placeholder="Telefón"
                                                    name="phone"
                                                    value={formik.values.phone}
                                                    onChange={formik.handleChange}
                                                    onBlur={formik.handleBlur}
                                                />
                                                {renderError('phone')}
                                            </div>
                                            <div className="flex-1">
                                                <input
                                                    className={cn(inputBase, errorClass('email'))}
                                                    type="email"
                                                    placeholder="Email"
                                                    name="email"
                                                    value={formik.values.email}
                                                    onChange={formik.handleChange}
                                                    onBlur={formik.handleBlur}
                                                />
                                                {renderError('email')}
                                            </div>
                                        </div>
                                        <div className="flex-1">
                                            <select
                                                className={cn(inputBase, errorClass('subject'))}
                                                name="subject"
                                                value={formik.values.subject}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                            >
                                                <option value="">Vyberte predmet</option>
                                                {SUBJECTS.map((subject, i) => (
                                                    <option value={subject}>{subject}</option>
                                                ))}
                                            </select>
                                            {renderError('subject')}
                                        </div>
                                        <div className="flex-1">
                                            <textarea
                                                name="message"
                                                className={cn(
                                                    inputBase,
                                                    'py-[12px] min-h-[120px] resize-none',
                                                    errorClass('message'),
                                                )}
                                                value={formik.values.message}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                            />{' '}
                                            {renderError('message')}
                                        </div>
                                    </div>
                                    <div className="mt-4 flex flex-col items-start justify-between gap-5 md:flex-row md:items-center">
                                        <div>
                                            <label
                                                className={cn(
                                                    'flex shrink-0 cursor-pointer items-center gap-[9px]',
                                                    'text-[12px]',
                                                    // 'text-[#000]',
                                                )}
                                            >
                                                <input
                                                    type="checkbox"
                                                    name="agreed"
                                                    checked={formik.values.agreed}
                                                    onChange={formik.handleChange}
                                                    onBlur={formik.handleBlur}
                                                />

                                                <span className="text-[12px]">
                                                    Súhlasím so spracovaním{' '}
                                                    <a href="#" className="underline">
                                                        osobných údajov
                                                    </a>
                                                    .
                                                </span>
                                            </label>
                                            {renderError('agreed')}
                                        </div>

                                        <button
                                            type="submit"
                                            className={cn(
                                                'ml-auto mb-[10px] shrink-0 whitespace-nowrap rounded-full',
                                                'bg-black px-[28px] py-[13px]',
                                                'text-[13.5px] font-semibold text-white',
                                                'transition-all duration-200',
                                            )}
                                        >
                                            Odoslať
                                        </button>
                                        {submitError && (
                                            <div className="w-full">
                                                <p className="mb-[10px] text-center text-[15px] font-[500] text-[#d32f2f]">
                                                    {submitError}
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                </form>
                            )}
                        </div>
                        <iframe
                            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2662.4604848498534!2d17.124466876938577!3d48.13992695099687!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x476c893dd89f79c5%3A0x63ec688e8fd8f277!2sPribinova%2019276%2F38A%2C%20811%2009%20Bratislava-Ru%C5%BEinov-Ru%C5%BEinov!5e0!3m2!1ssk!2ssk!4v1779813221495!5m2!1ssk!2ssk"
                            className={cn(
                                'w-full h-[450px] grayscale contrast-125 brightness-90 rounded-[0_0_15px_15px] lg:absolute lg:h-[100%] lg:w-[100%] lg:rounded-[20px]',
                                'xl:rounded-[25px]',
                            )}
                            style={{ border: 0 }}
                            allowFullScreen
                            loading="lazy"
                            referrerPolicy="no-referrer-when-downgrade"
                        ></iframe>
                    </div>
                </div>
            </section>

            <section className="relative py-[80px] md:py-[90px] xl:py-[110px]">
                <div className="cust-container">
                    <div className="relative grid grid-cols-1 md:grid-cols-3 xl:grid-cols-[46fr_28fr_28fr]">
                        <div className="relative">
                            <div className={cardLeftShadow} />
                            <div className="relative bg-white p-[30px] flex flex-col gap-[35px] xl:p-[50px]">
                                <img
                                    className="w-[160px] xl:w-[180px]"
                                    src={`${BRAND_PATH}/realico_logo_b_y_dot.svg`}
                                    alt="Realico"
                                />

                                <div className="leading-1.8 text-[15px] flex flex-col gap-[5px] md:text-[14px]">
                                    <div>
                                        <strong style={{ fontWeight: 700 }}>Email:</strong>{' '}
                                        <a
                                            href="mailto:kontakt@realico.sk"
                                            style={{ color: '#444' }}
                                        >
                                            kontakt@realico.sk
                                        </a>
                                    </div>

                                    <div>
                                        <strong style={{ fontWeight: 700 }}>Telefón:</strong>{' '}
                                        <a href="tel:+421905905905" style={{ color: '#444' }}>
                                            +421 905 905 905
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="relative">
                            <div className={cardLeftShadow} />
                            <div className="relative bg-white p-[30px] xl:p-[50px]">
                                <h3 className="mb-4 text-[17px] font-bold md:mt-[0px] md:text-[16px] xl:text-[17px]">
                                    Prevádzkovateľ:
                                </h3>
                                <ul className="space-y-2 text-[15px] leading-[1.4] text-[#444] md:text-[14px] md:mt-[20px] xl:text-[15px]">
                                    <li>Proply j.s.a.</li>
                                    <li>Pribinova 19276/38A</li>
                                    <li>Bratislava - Ružinov 811 09</li>
                                </ul>
                            </div>
                        </div>
                        <div className="relative">
                            <div className={cardLeftShadow} />
                            <div className="relative bg-white p-[30px] xl:p-[50px]">
                                <h3 className="mb-4 text-[17px] font-bold md:mt-[0px] md:text-[16px] xl:text-[17px]">
                                    Fakturačné údaje:
                                </h3>
                                <ul className="space-y-2 text-[15px] leading-[1.4] text-[#444] md:text-[14px] md:mt-[20px] xl:text-[15px]">
                                    <li>IČO: 50 020 161</li>
                                    <li>DIČ: 21 20 153 101</li>
                                    <li>IČ DPH: SK2120153101</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
